import { useUser } from "@supabase/auth-helpers-react";
import { Button, Col, Layout, Menu, MenuProps, Row, Space, Tooltip } from "antd";
import { ReactNode } from "react";
import { AuthCheck } from "@/components/authentication/AuthCheck";
import { AccountDropdownButton } from "@/components/buttons/AccountDropdownButton";
import { HelpDropdownButton } from "@/components/buttons/HelpDropdownButton";
import { AppName } from "@/components/layouts/AppName";
import styles from "@/components/layouts/HubLayout.module.scss";
import { useSidebar } from "@/components/layouts/SidebarContext";
import { XdiAttribution } from "@/components/layouts/XdiAttribution";
import { AcceptTerms } from "@/components/overlays/AcceptTerms";
import { HubVersionMarker } from "@/components/status/HubVersionMarker";
import { getAestheticsConfig } from "@/features/branding";
import { SummaryStatus } from "@/features/pe_aldd";
import { useGetCurrentUserQuery } from "@/store/services/supabase";
import { OrgKeyExchange } from "@/utils/hooks/useKeys/OrgKeyExchange";
import ArrowLeftOutlined from "@ant-design/icons/es/icons/ArrowLeftOutlined";
import MenuFoldOutlined from "@ant-design/icons/es/icons/MenuFoldOutlined";
import MenuUnfoldOutlined from "@ant-design/icons/es/icons/MenuUnfoldOutlined";
import classNames from "classnames";
import Link from "next/link";
import { useToolMenu } from "./hooks/useToolMenu";
const {
  Content,
  Sider
} = Layout;
export interface HubShellProps {
  children: ReactNode;
  disableCollapse?: boolean;
  /** Optional content rendered in the top nav bar, to the left of the app name. */
  navExtra?: ReactNode;
}

// Includes only top navbar and content area with none of the guards
export const HubShell = ({
  children,
  disableCollapse = false,
  navExtra
}: HubShellProps) => {
  const {
    data: user
  } = useGetCurrentUserQuery();
  const {
    isSidebarCollapsed,
    toggleSidebar
  } = useSidebar();
  return <Layout className={classNames(styles.layout, "absolute inset-0 overflow-hidden")} data-sentry-element="Layout" data-sentry-component="HubShell" data-sentry-source-file="HubLayout.tsx">
      <Row className="h-12 max-h-12 min-h-12 border-b border-gray-200" data-sentry-element="Row" data-sentry-source-file="HubLayout.tsx">
        <div className="flex w-full items-center justify-between pl-2 pr-4">
          <div className={`flex items-center justify-center gap-2 ${disableCollapse ? "" : ""}`}>
            {!disableCollapse && <div className="flex w-[32px] items-center justify-center">
                <Button type="text" icon={isSidebarCollapsed ? <MenuUnfoldOutlined className="text-xl" /> : <MenuFoldOutlined className="text-xl" />} onClick={toggleSidebar} />
              </div>}
            {navExtra && <div className="flex w-[32px] items-center justify-center">{navExtra}</div>}
            <AppName data-sentry-element="AppName" data-sentry-source-file="HubLayout.tsx" />
          </div>
          <Space data-sentry-element="Space" data-sentry-source-file="HubLayout.tsx">
            <SummaryStatus data-sentry-element="SummaryStatus" data-sentry-source-file="HubLayout.tsx" />

            {!!user && <div className="flex gap-3">
                <AccountDropdownButton {...user} />
              </div>}
          </Space>
        </div>
      </Row>
      <Layout className="mx-auto my-0 w-full p-0" data-sentry-element="Layout" data-sentry-source-file="HubLayout.tsx">{children}</Layout>
    </Layout>;
};
export interface HubLayoutProps extends HubShellProps {
  bypassAuthCheck?: boolean;
  sidebarWidth?: number;
  maxContentWidth?: string;
}

/**
 * The Hub Layout with just the header bar: the shell, plus the app-wide guards.
 */
export const HubLayout = ({
  bypassAuthCheck,
  children,
  disableCollapse = false,
  navExtra
}: HubLayoutProps) => {
  const layout = <OrgKeyExchange>
      <AcceptTerms />
      <HubShell disableCollapse={disableCollapse} navExtra={navExtra}>
        {children}
      </HubShell>
    </OrgKeyExchange>;
  return bypassAuthCheck ? layout : <AuthCheck data-sentry-element="AuthCheck" data-sentry-component="HubLayout" data-sentry-source-file="HubLayout.tsx">{layout}</AuthCheck>;
};

/**
 * A bare Hub layout for tools that need to own the entire viewport (no
 * sidebar, no top bar). Preserves the app-wide guards (auth, org key
 * exchange, terms acceptance) that HubLayout provides, without any chrome.
 */
export const HubLayoutBare = ({
  children
}: {
  children: ReactNode;
}) => <AuthCheck data-sentry-element="AuthCheck" data-sentry-component="HubLayoutBare" data-sentry-source-file="HubLayout.tsx">
    <OrgKeyExchange data-sentry-element="OrgKeyExchange" data-sentry-source-file="HubLayout.tsx">
      <AcceptTerms data-sentry-element="AcceptTerms" data-sentry-source-file="HubLayout.tsx" />
      <div className="absolute inset-0 overflow-hidden">{children}</div>
    </OrgKeyExchange>
  </AuthCheck>;
export interface HubLayoutWithSidebarProps extends HubLayoutProps {
  /** Rendered in place of the usual nav menu -- e.g. an editor's form. */
  sidebar: ReactNode;
}

/**
 * The Hub Layout with the top bar and guards intact, but an arbitrary sidebar in place
 * of the nav menu and a full-width content area. For full-screen editors where the
 * sidebar *is* the form and the content pane owns its own height.
 */
export const HubLayoutWithSidebar = ({
  sidebar,
  children,
  sidebarWidth = 420,
  maxContentWidth,
  disableCollapse = false,
  ...props
}: HubLayoutWithSidebarProps) => {
  const {
    isSidebarCollapsed
  } = useSidebar();
  return <HubLayout {...props} disableCollapse={disableCollapse} data-sentry-element="HubLayout" data-sentry-component="HubLayoutWithSidebar" data-sentry-source-file="HubLayout.tsx">
      <Sider className={classNames(styles.sidebar, "overflow-hidden border-r border-gray-200 bg-surface p-0")} width={sidebarWidth} collapsed={disableCollapse ? false : isSidebarCollapsed}
    // Collapse all the way out, so the toggle gives the preview the full viewport.
    collapsedWidth={0} trigger={null} data-sentry-element="Sider" data-sentry-source-file="HubLayout.tsx">
        {sidebar}
      </Sider>
      <Col className="h-full w-full overflow-hidden" data-sentry-element="Col" data-sentry-source-file="HubLayout.tsx">
        <Content className="h-full w-full p-0" style={maxContentWidth ? {
        maxWidth: maxContentWidth
      } : undefined} data-sentry-element="Content" data-sentry-source-file="HubLayout.tsx">
          {children}
        </Content>
      </Col>
    </HubLayout>;
};
export interface HubLayoutWithMenuProps extends HubLayoutProps {
  items: MenuProps["items"];
  onClick: (key: string) => void;
  selectedKey: string;
  customBackLink?: {
    url: string;
    text: string;
  };
  header?: ReactNode;
  hideBackLink?: boolean;
  disableCollapse?: boolean;
}

/**
 * The Hub Layout with a custom menu.
 */
export const HubLayoutWithMenu = ({
  items,
  onClick,
  selectedKey,
  children,
  customBackLink,
  header,
  hideBackLink = false,
  sidebarWidth = 256,
  maxContentWidth = "1200px",
  disableCollapse = false,
  ...props
}: HubLayoutWithMenuProps) => {
  const user = useUser();
  const {
    attribution
  } = getAestheticsConfig();
  const {
    isSidebarCollapsed
  } = useSidebar();
  const sidebarCollapsed = disableCollapse ? false : isSidebarCollapsed;
  const backLinkText = customBackLink?.text ?? (user ? "Back to Tools" : "Back to Log In");
  return <HubLayout {...props} disableCollapse={disableCollapse} data-sentry-element="HubLayout" data-sentry-component="HubLayoutWithMenu" data-sentry-source-file="HubLayout.tsx">
      <Sider className={classNames(styles.sidebar, "overflow-y-auto border-r border-gray-200 bg-surface p-0")} width={sidebarWidth} collapsed={sidebarCollapsed} collapsedWidth={48} trigger={null} data-sentry-element="Sider" data-sentry-source-file="HubLayout.tsx">
        <div>
          {!hideBackLink && <BackLink customBackLink={customBackLink} sidebarCollapsed={sidebarCollapsed} backLinkText={backLinkText} />}
          {header}
          <Menu className={classNames(styles.sidebarMenu, "border-0")} items={items} onClick={({
          key
        }) => onClick(key)} selectedKeys={[selectedKey]} mode="inline" inlineIndent={10} inlineCollapsed={sidebarCollapsed} data-sentry-element="Menu" data-sentry-source-file="HubLayout.tsx" />
        </div>
        <div>
          <HelpDropdownButton data-sentry-element="HelpDropdownButton" data-sentry-source-file="HubLayout.tsx" />
          {attribution === "poweredByXdi" && <div className={styles.attributionContainer}>
              <XdiAttribution mode="vertical" />
            </div>}
          <HubVersionMarker data-sentry-element="HubVersionMarker" data-sentry-source-file="HubLayout.tsx" />
        </div>
      </Sider>
      <Col className="w-full overflow-y-auto" data-sentry-element="Col" data-sentry-source-file="HubLayout.tsx">
        <Content className="mx-auto my-0 w-full p-0" style={{
        maxWidth: maxContentWidth
      }} data-sentry-element="Content" data-sentry-source-file="HubLayout.tsx">
          {children}
        </Content>
      </Col>
    </HubLayout>;
};

/**
 * The Hub Layout but with all available tools in the menu.
 */
export const HubLayoutWithToolMenu = ({
  children,
  maxContentWidth
}: HubLayoutProps) => {
  const {
    items,
    selectedKey,
    onClick
  } = useToolMenu();
  return <HubLayoutWithMenu items={items} onClick={onClick} selectedKey={selectedKey} hideBackLink={true} maxContentWidth={maxContentWidth} data-sentry-element="HubLayoutWithMenu" data-sentry-component="HubLayoutWithToolMenu" data-sentry-source-file="HubLayout.tsx">
      {children}
    </HubLayoutWithMenu>;
};
const BackLink = ({
  customBackLink,
  sidebarCollapsed,
  backLinkText
}: {
  customBackLink: {
    url: string;
    text: string;
  } | undefined;
  sidebarCollapsed: boolean;
  backLinkText: string;
}) => {
  return <Link href={customBackLink?.url ?? "/"} data-sentry-element="Link" data-sentry-component="BackLink" data-sentry-source-file="HubLayout.tsx">
      <div className={`${sidebarCollapsed ? "w-full justify-center" : ""} border-b border-gray-200 ease-out`}>
        <Tooltip title={sidebarCollapsed ? backLinkText : null} placement="right" data-sentry-element="Tooltip" data-sentry-source-file="HubLayout.tsx">
          <Button block type="text" className={`${sidebarCollapsed ? "w-full justify-center" : "flex-start w-full items-center"} rounded-none text-gray-500`} icon={<ArrowLeftOutlined />} data-sentry-element="Button" data-sentry-source-file="HubLayout.tsx">
            {sidebarCollapsed ? null : backLinkText}
          </Button>
        </Tooltip>
      </div>
    </Link>;
};